import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Production log suppression — Debug/Info-Logs abschalten, damit die Konsole
// im Live-Shop sauber bleibt. console.error/warn bleiben für echte Fehler.
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  // eslint-disable-next-line no-console
  console.log = noop;
  // eslint-disable-next-line no-console
  console.debug = noop;
  // eslint-disable-next-line no-console
  console.info = noop;
}

// App version for cache busting - increment on each deploy
const APP_VERSION = '9.0.0';
const ICON_VERSION = '2026053100';
// Auto-update check interval (10 minutes - balanced for shops)
const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000;

// Force refresh PWA icons
const refreshIcons = async () => {
  const storedIconVersion = localStorage.getItem('icon-version');
  if (storedIconVersion !== ICON_VERSION) {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const name of cacheNames) {
        const cache = await caches.open(name);
        const requests = await cache.keys();
        for (const request of requests) {
          if (request.url.includes('icon') ||
              request.url.includes('favicon') ||
              request.url.includes('manifest')) {
            await cache.delete(request);
          }
        }
      }
    }
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'REFRESH_ICONS' });
    }
    localStorage.setItem('icon-version', ICON_VERSION);
  }
};

// Detect version change and reload
const checkVersion = () => {
  const storedVersion = localStorage.getItem('app-version');
  if (storedVersion && storedVersion !== APP_VERSION) {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => names.forEach(name => caches.delete(name)));
    }
    localStorage.setItem('app-version', APP_VERSION);
    window.location.reload(true);
    return false;
  }
  localStorage.setItem('app-version', APP_VERSION);
  refreshIcons();
  return true;
};

// Register service worker with automatic update detection
const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');

      // Trigger update check
      const checkForUpdate = () => {
        registration.update().catch(() => {});
      };

      // Check for updates periodically (every 60s)
      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);

      // Check immediately when tab regains focus
      window.addEventListener('focus', checkForUpdate);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) checkForUpdate();
      });

      // When a new SW is found, activate it and reload smoothly
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New version available - tell it to take over immediately
            newWorker.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });

      // Listen for the new SW taking control and reload
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });

      // Listen for explicit update notifications from SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          // Soft reload to apply update
          if (!refreshing) {
            refreshing = true;
            window.location.reload();
          }
        }
      });
    } catch (err) {
      console.warn('SW registration failed:', err);
    }
  });
};

if (checkVersion()) {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
  registerServiceWorker();
}
